import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ArrowRight, LogIn } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { toggleTheme } from 'app/providers/themeSlice';
import { Button } from 'components/ui/Button';
import { BrandWordmark } from 'components/brand/BrandWordmark';
import { landing } from 'constants/copy';
import { ROUTES } from 'constants/routes';
import { useMarketingAuthNav } from 'features/marketing/hooks/useMarketingAuthNav';
import { AskGuruNavLabel } from 'features/marketing/components/AskGuruNavLabel';
import { cn } from 'utils/cn';

type NavLink = { label: string; type: 'route'; href: string };

const mainNavLinks: NavLink[] = [
  { label: landing.nav.home, href: ROUTES.HOME, type: 'route' },
  { label: landing.nav.programs, href: ROUTES.PROGRAMS, type: 'route' },
  { label: landing.nav.community, href: ROUTES.MARKETING_SANGHA, type: 'route' },
  { label: landing.nav.about, href: ROUTES.ABOUT, type: 'route' },
  { label: landing.nav.contact, href: ROUTES.CONTACT, type: 'route' },
];

const askGuruHref = ROUTES.MARKETING_ASK_GURU;

export function MarketingNav() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const resolved = useAppSelector((s) => s.theme.resolved);
  const { goToLogin } = useMarketingAuthNav();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    return undefined;
  }, [mobileOpen]);

  const handleNavClick = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const askGuruActive = location.pathname === askGuruHref;

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-[var(--z-header)] border-b pt-safe-top',
          location.pathname === ROUTES.HOME
            ? 'border-transparent bg-white text-content dark:bg-black dark:text-content'
            : 'border-border bg-surface'
        )}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:gap-4 sm:px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl p-2 -ml-2 transition-colors hover:bg-surface-secondary lg:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link
              to={ROUTES.HOME}
              className="group shrink-0"
              aria-label="Takshify - The Digital Takshashila"
            >
              <BrandWordmark />
            </Link>
          </div>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {mainNavLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-surface-secondary hover:text-content',
                  isActive ? 'text-accent-strong' : 'text-content-secondary',
                )}
              >
                {link.label}
              </Link>
            );
            })}
          </nav>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <Link
              to={askGuruHref}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors hover:bg-surface-secondary/60 md:min-h-0 md:min-w-0 md:px-1 md:py-1"
              aria-label={landing.nav.aiGuru}
            >
              <AskGuruNavLabel active={askGuruActive} iconOnly="responsive" />
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch(toggleTheme())}
              aria-label="Toggle theme"
              className="min-h-[44px] min-w-[44px] shrink-0 px-2.5 md:min-h-9 md:min-w-0"
            >
              {resolved === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              size="sm"
              onClick={() => goToLogin()}
              aria-label={landing.nav.login}
              className="btn-marketing-signin group h-9 min-h-[44px] min-w-[44px] shrink-0 gap-1.5 px-2.5 shadow-none md:min-w-0 md:px-4 md:text-[13px]"
            >
              <LogIn className="h-4 w-4 md:hidden" aria-hidden />
              <span className="hidden md:inline">{landing.nav.login}</span>
              <ArrowRight
                className="hidden h-3.5 w-3.5 opacity-80 transition-transform duration-300 group-hover:translate-x-0.5 md:block"
                aria-hidden
              />
            </Button>
          </div>
        </div>
      </header>

      {mobileOpen ? (
        <>
          <button
            type="button"
            className={cn(
              'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden',
              visible ? 'opacity-100' : 'opacity-0',
            )}
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <div
            ref={panelRef}
            className={cn(
              'fixed inset-y-0 left-0 z-50 w-[min(100%,20rem)] border-r border-border bg-surface p-4 shadow-modal transition-transform duration-300 lg:hidden',
              visible ? 'translate-x-0' : '-translate-x-full',
            )}
          >
            <nav className="flex flex-col gap-1 pt-2" aria-label="Mobile">
              {mainNavLinks.map((link) => {
                const isActive = location.pathname === link.href;
                return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={handleNavClick}
                  className={cn(
                    'rounded-xl px-4 py-3 text-sm font-medium text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content',
                    isActive && 'bg-surface-secondary text-accent-strong',
                  )}
                >
                  {link.label}
                </Link>
              );
              })}
            </nav>
            <div className="mt-6 flex flex-col gap-2 border-t border-border pt-6">
              <Link
                to={askGuruHref}
                onClick={handleNavClick}
                className="flex justify-center rounded-xl px-4 py-2 transition-colors hover:bg-surface-secondary/60"
              >
                <AskGuruNavLabel active={askGuruActive} />
              </Link>
              <Button
                onClick={() => { goToLogin(); setMobileOpen(false); }}
                className="btn-marketing-signin group h-11 min-h-11 w-full gap-2 text-sm shadow-none"
              >
                <span>{landing.nav.login}</span>
                <ArrowRight className="h-4 w-4 opacity-80 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden />
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
